import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Target,
    ArrowRight,
    ArrowLeft,
    Brain,
    DollarSign,
    Shield,
    Zap,
    Briefcase,
    BookOpen
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import InlineSpinner from '../components/InlineSpinner';
import { toast } from 'react-hot-toast';
import { careerAPI, userAPI } from '../utils/api';
import ValuesPriority from '../components/journey/ValuesPriority';
import JourneyProgress from '../components/journey/JourneyProgress';

const RoadmapSetupPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [baseProfile, setBaseProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const totalSteps = 4;

    const industryOptions = [
        'Technology', 'Finance', 'Healthcare', 'Education', 'E-commerce',
        'Marketing', 'Consulting', 'Manufacturing', 'Retail', 'Media',
        'Gaming', 'Automotive'
    ];

    const techDomainOptions = [
        'Frontend Web', 'Backend Web', 'Full Stack', 'iOS App Dev',
        'Android App Dev', 'AI & Machine Learning', 'Data Science',
        'Cloud & DevOps', 'Cybersecurity', 'Game Development',
        'System Design', 'ML Architecture', 'Technical Program Manager (TPM)',
        'Site Reliability Engineering (SRE)'
    ];

    // Load saved data and verify base profile
    // Try localStorage first, then fall back to API
    useEffect(() => {
        const loadProfile = async () => {
            try {
                // 1. Try localStorage first (fast path)
                const storedProfileStr = localStorage.getItem('userProfile');
                if (storedProfileStr) {
                    const parsedProfile = JSON.parse(storedProfileStr);
                    if (parsedProfile && Object.keys(parsedProfile).length > 0) {
                        setBaseProfile(parsedProfile);
                        setProfileLoading(false);
                        return;
                    }
                }

                // 2. Fallback: fetch from backend API (OnboardingPage saves here)
                const res = await userAPI.getProfile();
                if (res?.profile && Object.keys(res.profile).length > 0) {
                    // Sync to localStorage so future visits are fast
                    localStorage.setItem('userProfile', JSON.stringify(res.profile));
                    setBaseProfile(res.profile);
                    setProfileLoading(false);
                    return;
                }

                // 3. No profile found anywhere — redirect to onboarding
                toast('Please complete your baseline profile first.', { icon: 'ℹ️' });
                navigate('/onboarding');
            } catch (e) {
                console.error('Error loading profile:', e);
                toast('Please complete your baseline profile first.', { icon: 'ℹ️' });
                navigate('/onboarding');
            }
        };

        loadProfile();
    }, [navigate]);

    const isStudent = baseProfile?.identity === 'student';

    const [formData, setFormData] = useState({
        careerGoals: '',
        primaryMotivation: '',
        careerValuesPriority: [],
        industries: [],
        salaryExpectation: '',
        biggestConstraint: '',
        riskTolerance: '',
        satisfactionScores: {},
        timeAvailable: '',

        // Student specific fields
        learningObjective: '',
        techDomains: []
    });

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleArrayToggle = (field, item) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(item)
                ? prev[field].filter(i => i !== item)
                : [...prev[field], item]
        }));
    };

    const handleSatisfactionChange = (dimension, score) => {
        setFormData(prev => ({
            ...prev,
            satisfactionScores: {
                ...prev.satisfactionScores,
                [dimension]: score
            }
        }));
    };

    const isStepValid = (step) => {
        switch (step) {
            case 1:
                if (isStudent) return formData.learningObjective && formData.careerValuesPriority.length >= 3;
                return formData.careerGoals && formData.primaryMotivation && formData.careerValuesPriority.length >= 3;
            case 2:
                if (isStudent) return formData.techDomains.length >= 1;
                return formData.industries.length >= 1 && formData.salaryExpectation;
            case 3:
                return !!formData.riskTolerance;
            case 4:
                return !!formData.timeAvailable;
            default: return false;
        }
    };

    const nextStep = () => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            // Merge base profile with the new roadmap context
            const userProfile = {
                ...baseProfile,
                goals: isStudent ? [formData.learningObjective] : (formData.careerGoals ? [formData.careerGoals] : baseProfile.goals || []),
                preferred_industries: isStudent ? formData.techDomains : formData.industries,
                target_salary: isStudent ? null : (parseInt(formData.salaryExpectation) || null),

                career_values_priority: formData.careerValuesPriority,
                satisfaction_scores: isStudent ? {} : formData.satisfactionScores,
                learning_preferences: {
                    commitment_hours_per_week: formData.timeAvailable,
                },
                constraints: formData.biggestConstraint
                    ? { primary: formData.biggestConstraint }
                    : {},
                motivations: isStudent ? 'Learning and Preparation' : (formData.primaryMotivation || ''),
                career_change_intent: isStudent ? 'explore' : (
                    formData.primaryMotivation === 'Grow in my current path'
                        ? 'grow'
                        : formData.primaryMotivation === 'Switch to a new domain'
                            ? 'transition'
                            : formData.primaryMotivation === 'Optimize work-life balance'
                                ? 'optimize'
                                : 'explore'),
                risk_tolerance: (formData.riskTolerance || 'moderate').toLowerCase(),
                transition_type: isStudent ? 'grow' : (
                    formData.primaryMotivation === 'Switch to a new domain'
                        ? 'transition'
                        : formData.primaryMotivation === 'Grow in my current path'
                            ? 'grow'
                            : 'optimize'),
            };

            console.log('Calling suggestCareers with merged profile:', userProfile);
            const suggestions = await careerAPI.suggestCareers({ profile: userProfile });
            console.log('Received suggestions:', suggestions);

            // Store merged profile and suggestions
            localStorage.setItem('userProfile', JSON.stringify(userProfile));
            localStorage.setItem('careerSuggestions', JSON.stringify(suggestions));

            toast.success('Roadmap generated based on your profile and context!');

            const returnTo = location.state?.returnTo;
            if (returnTo) {
                navigate(returnTo);
            } else {
                navigate('/free-results');
            }
        } catch (error) {
            console.error('Error submitting roadmap setup:', error);
            toast.error(error.message || 'Failed to generate career insights.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!baseProfile) {
        return <LoadingSpinner />;
    }

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                {isStudent ? <BookOpen className="w-8 h-8 text-white" /> : <Target className="w-8 h-8 text-white" />}
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2 font-mono">
                                {isStudent ? 'YOUR LEARNING PATH' : 'YOUR ROADMAP GOALS'}
                            </h2>
                            <p className="text-slate-400">
                                {isStudent ? 'What are you focusing on?' : 'What are we optimizing for?'}
                            </p>
                        </div>

                        <div className="max-w-2xl mx-auto space-y-8">
                            {isStudent ? (
                                <div>
                                    <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                        What is your primary learning objective? *
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {[
                                            'Prepare for Campus Placements',
                                            'Master Data Structures & Algorithms',
                                            'Learn a specific tech stack',
                                            'General Upskilling'
                                        ].map(option => (
                                            <button
                                                key={option}
                                                onClick={() => handleInputChange('learningObjective', option)}
                                                className={`p-4 rounded-xl border-2 text-left transition-all ${formData.learningObjective === option
                                                    ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                                                    : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                    }`}
                                            >
                                                <div className="font-semibold text-sm font-mono">{option}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-300 mb-3 font-mono uppercase">
                                            What's your main career goal? *
                                        </label>
                                        <textarea
                                            value={formData.careerGoals}
                                            onChange={(e) => handleInputChange('careerGoals', e.target.value)}
                                            rows={3}
                                            className="w-full px-6 py-4 bg-[#0F131F] border-2 border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none font-mono"
                                            placeholder="e.g., Become a senior developer, transition to product management..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                            What's driving this change? *
                                        </label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {[
                                                'Grow in my current path',
                                                'Switch to a new domain',
                                                'Optimize work-life balance',
                                                'Figure out what fits me best'
                                            ].map(option => (
                                                <button
                                                    key={option}
                                                    onClick={() => handleInputChange('primaryMotivation', option)}
                                                    className={`p-4 rounded-xl border-2 text-left transition-all ${formData.primaryMotivation === option
                                                        ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300'
                                                        : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                        }`}
                                                >
                                                    <div className="font-semibold text-sm font-mono">{option}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                    What matters most to you? (Rank at least 3) *
                                </label>
                                <ValuesPriority
                                    rankedValues={formData.careerValuesPriority}
                                    onRankChange={(values) => handleInputChange('careerValuesPriority', values)}
                                />
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                {isStudent ? <Zap className="w-8 h-8 text-white" /> : <Briefcase className="w-8 h-8 text-white" />}
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2 font-mono">
                                {isStudent ? 'TARGET TECH DOMAINS' : 'TARGET INDUSTRIES'}
                            </h2>
                            <p className="text-slate-400">
                                {isStudent ? 'What stacks do you want to master?' : 'Where do you want to work?'}
                            </p>
                        </div>

                        <div className="max-w-3xl mx-auto space-y-8">
                            {isStudent ? (
                                <div>
                                    <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                        Select Your Tech Domains *
                                    </label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {techDomainOptions.map((domain) => (
                                            <button
                                                key={domain}
                                                onClick={() => handleArrayToggle('techDomains', domain)}
                                                className={`p-3 rounded-xl border-2 transition-all text-sm ${formData.techDomains.includes(domain)
                                                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-300'
                                                    : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                    }`}
                                            >
                                                <span className="font-mono">{domain}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                            Industries You're Interested In *
                                        </label>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {industryOptions.map((industry) => (
                                                <button
                                                    key={industry}
                                                    onClick={() => handleArrayToggle('industries', industry)}
                                                    className={`p-3 rounded-xl border-2 transition-all text-sm ${formData.industries.includes(industry)
                                                        ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                                                        : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                        }`}
                                                >
                                                    <span className="font-mono">{industry}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-300 mb-3 font-mono uppercase">
                                            <DollarSign className="w-4 h-4 inline mr-2" />
                                            Target Annual Salary (INR) *
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.salaryExpectation}
                                            onChange={(e) => handleInputChange('salaryExpectation', e.target.value)}
                                            className="w-full px-6 py-4 bg-[#0F131F] border-2 border-white/10 rounded-xl text-white text-lg placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all font-mono"
                                            placeholder="e.g., 1500000"
                                        />
                                        <p className="text-xs text-slate-500 mt-2 font-mono">This helps us suggest paths that match your financial goals</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                <Shield className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2 font-mono">REALITY CHECK</h2>
                            <p className="text-slate-400">Being honest helps us be realistic</p>
                        </div>

                        <div className="max-w-2xl mx-auto space-y-8">
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                    What's your biggest constraint right now?
                                </label>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {['Time', 'Money', 'Confidence', 'Clarity on options'].map(option => (
                                        <button
                                            key={option}
                                            onClick={() => handleInputChange('biggestConstraint', option)}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${formData.biggestConstraint === option
                                                ? 'border-yellow-500 bg-yellow-500/20 text-yellow-300'
                                                : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                }`}
                                        >
                                            <div className="font-semibold text-sm font-mono">{option}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                    How comfortable are you taking career risks? *
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { value: 'low', label: 'Low Risk', desc: 'Prefer stability & predictability' },
                                        { value: 'moderate', label: 'Moderate', desc: 'Open to calculated bets' },
                                        { value: 'high', label: 'High Risk', desc: 'Comfortable with bold moves' }
                                    ].map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => handleInputChange('riskTolerance', option.value)}
                                            className={`p-4 rounded-xl border-2 text-left transition-all ${formData.riskTolerance === option.value
                                                ? 'border-red-500 bg-red-500/20 text-red-300'
                                                : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                }`}
                                        >
                                            <div className="font-semibold text-sm font-mono">{option.label}</div>
                                            <div className="text-xs opacity-75 font-mono mt-1">{option.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {!isStudent && (
                                <div>
                                    <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                        Rate Your Current Satisfaction (1-5)
                                    </label>
                                    <div className="space-y-4">
                                        {[
                                            { key: 'current_role', label: 'Current Role' },
                                            { key: 'growth', label: 'Growth Opportunities' },
                                            { key: 'compensation', label: 'Compensation' }
                                        ].map(dim => (
                                            <div key={dim.key} className="flex items-center justify-between bg-[#0F131F] rounded-xl p-4 border border-white/5">
                                                <span className="text-slate-300 font-mono text-sm">{dim.label}</span>
                                                <div className="flex space-x-2">
                                                    {[1, 2, 3, 4, 5].map(score => (
                                                        <button
                                                            key={score}
                                                            onClick={() => handleSatisfactionChange(dim.key, score)}
                                                            className={`w-10 h-10 rounded-lg font-mono font-semibold transition-all ${formData.satisfactionScores[dim.key] === score
                                                                ? score <= 2 ? 'bg-red-500 text-white'
                                                                    : score === 3 ? 'bg-yellow-500 text-black'
                                                                        : 'bg-emerald-500 text-white'
                                                                : 'bg-white/5 text-slate-400 hover:bg-white/10'
                                                                }`}
                                                        >
                                                            {score}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-8">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                                <Zap className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2 font-mono">YOUR COMMITMENT</h2>
                            <p className="text-slate-400">How fast do you want to move?</p>
                        </div>

                        <div className="max-w-2xl mx-auto space-y-8">
                            <div>
                                <label className="block text-sm font-semibold text-slate-300 mb-4 font-mono uppercase">
                                    Time Available for Learning (per week) *
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[
                                        { value: '4-6 hours', label: 'Light', desc: '4-6 hours/week' },
                                        { value: '8-12 hours', label: 'Dedicated', desc: '8-12 hours/week' },
                                        { value: '15-20 hours', label: 'Intensive', desc: '15-20 hours/week' }
                                    ].map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => handleInputChange('timeAvailable', option.value)}
                                            className={`p-6 rounded-xl border-2 transition-all ${formData.timeAvailable === option.value
                                                ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300'
                                                : 'border-white/10 bg-[#0F131F] text-slate-300 hover:border-white/20'
                                                }`}
                                        >
                                            <div className="font-semibold mb-1 font-mono">{option.label}</div>
                                            <div className="text-sm opacity-75 font-mono">{option.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-[#0B0F19] relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>

            <div className="relative z-10 max-w-5xl mx-auto px-4 py-8 mt-16">
                <JourneyProgress
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    showGlimpse={false}
                />

                <div className="bg-[#0F131F]/50 border border-white/5 rounded-3xl p-6 md:p-10 mb-8 backdrop-blur-sm">
                    {renderStepContent()}
                </div>

                <div className="flex items-center justify-between">
                    <button
                        onClick={prevStep}
                        disabled={currentStep === 1}
                        className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all font-mono ${currentStep === 1
                            ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                            : 'bg-slate-700 text-white hover:bg-slate-600 border border-white/10'
                            }`}
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span>BACK</span>
                    </button>

                    {currentStep < totalSteps ? (
                        <button
                            onClick={nextStep}
                            disabled={!isStepValid(currentStep)}
                            className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-semibold transition-all font-mono ${isStepValid(currentStep)
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:scale-105 shadow-lg'
                                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                }`}
                        >
                            <span>CONTINUE</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={isLoading || !isStepValid(currentStep)}
                            className={`flex items-center space-x-2 px-8 py-4 rounded-xl font-semibold transition-all font-mono ${!isLoading && isStepValid(currentStep)
                                ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:scale-105 shadow-lg'
                                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                }`}
                        >
                            {isLoading ? (
                                <>
                                    <InlineSpinner size="sm" />
                                    <span>PROCESSING...</span>
                                </>
                            ) : (
                                <>
                                    <Brain className="w-5 h-5" />
                                    <span>GENERATE ROADMAP</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoadmapSetupPage;
